/**
 * Départements sur les annonces (dont « Je cherche » multi-zones).
 */

export function normalizeDeptChampKey(nom) {
  return String(nom || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

export function isDepartementChampKey(nom) {
  const k = normalizeDeptChampKey(nom);
  return k === "departement" || k === "département" || k.includes("departement");
}

/** Extrait les codes département (2–3 chiffres) depuis « 63 », « 63-43-03 », « 63, 43, 03 ». */
export function parseDepartmentCodes(text) {
  const t = String(text ?? "").trim();
  if (!t) return [];
  if (/^\d{2,3}$/.test(t)) return [t];
  const codes = [];
  const parts = t.split(/[\s,;/\-–—·]+/).map((s) => s.trim()).filter(Boolean);
  for (const p of parts) {
    if (/^\d{2,3}$/.test(p)) codes.push(p);
    else {
      const m = p.match(/\b(\d{2,3})\b/g);
      if (m) codes.push(...m);
    }
  }
  return [...new Set(codes)];
}

/** Libellé affiché (multi-départements : « 63 · 43 · 03 »). */
export function formatDepartmentDisplay(text) {
  const t = String(text ?? "").trim();
  if (!t) return null;
  const codes = parseDepartmentCodes(t);
  if (codes.length > 0) return codes.join(" · ");
  return t;
}

/** Un seul code depuis CP ou code court (filtres legacy). */
export function extractSingleDeptFromText(text) {
  const t = String(text || "").trim();
  if (!t) return null;
  const codes = parseDepartmentCodes(t);
  if (codes.length === 1) return codes[0];
  if (/^\d{2,3}$/.test(t)) return t;
  const m = t.match(/\b(\d{5})\b/);
  if (!m) return null;
  const cp = m[1];
  if (cp.startsWith("97") || cp.startsWith("98")) return cp.slice(0, 3);
  return cp.slice(0, 2);
}

function dossierCustomFields(dossier) {
  return dossier?.custom_fields && typeof dossier.custom_fields === "object" && !Array.isArray(dossier.custom_fields)
    ? dossier.custom_fields
    : {};
}

/** Valeur brute du champ « Département » dans custom_fields (sans repli sur location). */
export function findDepartementFieldRaw(dossier) {
  const cf = dossierCustomFields(dossier);
  for (const k of Object.keys(cf)) {
    if (!isDepartementChampKey(k)) continue;
    const raw = String(cf[k] ?? "").trim();
    if (raw) return { key: k, raw };
  }
  return { key: null, raw: null };
}

/**
 * Libellé à afficher sur cartes / fiches (priorité : champ département explicite).
 */
export function getDeptDisplayLabel(dossier) {
  const fromField = findDepartementFieldRaw(dossier);
  if (fromField.raw) return formatDepartmentDisplay(fromField.raw);

  const direct =
    dossier?.departement ??
    dossier?.department ??
    dossier?.dept ??
    dossier?.department_code ??
    dossier?.departement_code ??
    null;
  if (direct != null && String(direct).trim() !== "") {
    return formatDepartmentDisplay(direct) || extractSingleDeptFromText(direct);
  }

  const cf = dossierCustomFields(dossier);
  for (const k of Object.keys(cf)) {
    const nk = normalizeDeptChampKey(k);
    if (nk.includes("adresse") || nk.includes("address") || nk.includes("code postal") || nk.includes("postal")) {
      const v = extractSingleDeptFromText(cf[k]);
      if (v) return v;
    }
  }

  const loc = dossier?.location;
  if (loc != null && String(loc).trim() !== "") {
    return formatDepartmentDisplay(loc) || extractSingleDeptFromText(loc);
  }
  return null;
}

/** Au moins un département en commun (critères recherche). */
export function departmentValuesMatch(wantRaw, offerRaw) {
  const want = parseDepartmentCodes(wantRaw);
  const offer = parseDepartmentCodes(offerRaw);
  if (want.length === 0) return true;
  if (offer.length === 0) return String(wantRaw ?? "").trim() === String(offerRaw ?? "").trim();
  return want.some((c) => offer.includes(c));
}

export function isDepartementCriterionKey(key) {
  return isDepartementChampKey(key);
}

/** Codes département pour badges UI (ex. 63 · 43 · 03 → ['63','43','03']). */
export function getDeptChipsForDisplay(dossier) {
  const label = getDeptDisplayLabel(dossier);
  if (!label) return [];
  const codes = parseDepartmentCodes(label);
  if (codes.length > 0) return codes;
  return [label];
}
